import React, { useState, useEffect } from 'react';
import type { MenuItem, Category } from '../../types';
import PlusIcon from '../icons/PlusIcon';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';
import { uploadMenuItemImage } from '../../lib/menuImageUpload';
import {
  channelAvailabilityForNewItem,
  channelsLockedByCategory,
  readChannelAvailability,
} from '../../lib/menuChannelAvailability';
import MenuItemImage from './MenuItemImage';
import MenuChannelAvailabilityFields from './MenuChannelAvailabilityFields';
import { COMMON_ALLERGENS, DIETARY_TAGS, type DietaryTag } from '../../lib/dietaryTags';

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

interface MenuItemModalProps {
  item: MenuItem | null;
  categories: Category[];
  /** Category open on the menu page. New items start in this category. */
  defaultCategoryId?: number | string;
  onClose: () => void;
  onSave: (item: MenuItem) => void;
}

function categoryForId(categories: Category[], categoryId: number | string | undefined): Category | undefined {
  return categories.find((category) => String(category.id) === String(categoryId));
}

const MenuItemModal: React.FC<MenuItemModalProps> = ({ item, categories, defaultCategoryId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    categoryId: categories[0]?.id ?? 0,
    imageUrl: '',
    imageUrlThumb: '' as string | undefined,
    description: '',
    isAvailable: true,
    isSignatureDish: false,
    meatOptions: [] as string[],
    hasDailyAvailability: false,
    dailyAvailabilityLimit: 0,
    dietaryTags: [] as DietaryTag[],
    allergens: [] as string[],
    allergyWarning: '',
    soldByWeight: false,
    weightFishOptions: [] as string[],
  });
  const [id, setId] = useState<number | null>(null);
  const [meatOptionInput, setMeatOptionInput] = useState('');
  const [fishNameInput, setFishNameInput] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [channelAvailability, setChannelAvailability] = useState(readChannelAvailability(undefined));

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        price: item.price,
        categoryId: item.categoryId,
        imageUrl: item.imageUrl,
        imageUrlThumb: item.imageUrlThumb ?? '',
        description: item.description || '',
        isAvailable: item.isAvailable !== false,
        isSignatureDish: item.isSignatureDish || false,
        meatOptions: item.meatOptions || [],
        hasDailyAvailability: item.hasDailyAvailability || false,
        dailyAvailabilityLimit: item.dailyAvailabilityLimit || 0,
        dietaryTags: item.dietaryTags || [],
        allergens: item.allergens || [],
        allergyWarning: item.allergyWarning || '',
        soldByWeight: item.soldByWeight === true,
        weightFishOptions: item.weightFishOptions || [],
      });
      setId(item.id);
      setChannelAvailability(readChannelAvailability(item));
    } else {
        const startingCategory = categoryForId(categories, defaultCategoryId) || categories[0];
        setFormData({
            name: '',
            price: 0,
            categoryId: startingCategory?.id ?? 0,
            imageUrl: '',
            imageUrlThumb: '',
            description: '',
            isAvailable: true,
            isSignatureDish: false,
            meatOptions: [],
            hasDailyAvailability: false,
            dailyAvailabilityLimit: 0,
            dietaryTags: [],
            allergens: [],
            allergyWarning: '',
            soldByWeight: false,
            weightFishOptions: [],
        });
        setId(null);
        setChannelAvailability(channelAvailabilityForNewItem(startingCategory));
    }
  }, [item, categories, defaultCategoryId]);

  const nameKeyboard = useSoftKeyboardField({
    id: 'menu-item-name',
    type: 'alphanumeric',
    label: 'Item name',
    value: formData.name,
    onChange: (v) => setFormData((prev) => ({ ...prev, name: v })),
  });
  const priceKeyboard = useSoftKeyboardField({
    id: 'menu-item-price',
    type: 'numeric',
    label: 'Price',
    value: formData.price === 0 ? '' : String(formData.price),
    onChange: (v) => setFormData((prev) => ({ ...prev, price: parseFloat(v) || 0 })),
    manualValidator: (v) => v === '' || /^\d*\.?\d*$/.test(v),
  });
  const descriptionKeyboard = useSoftKeyboardField({
    id: 'menu-item-description',
    type: 'alphanumeric',
    label: 'Description',
    value: formData.description,
    onChange: (v) => setFormData((prev) => ({ ...prev, description: v })),
  });
  const dailyLimitKeyboard = useSoftKeyboardField({
    id: 'menu-item-daily-limit',
    type: 'numeric',
    label: 'Daily availability limit',
    value: formData.dailyAvailabilityLimit === 0 ? '' : String(formData.dailyAvailabilityLimit),
    onChange: (v) => setFormData((prev) => ({ ...prev, dailyAvailabilityLimit: parseInt(v, 10) || 0 })),
    manualValidator: (v) => v === '' || /^\d*$/.test(v),
  });
  const meatOptionKeyboard = useSoftKeyboardField({
    id: 'menu-item-meat-option',
    type: 'alphanumeric',
    label: 'Meat option',
    value: meatOptionInput,
    onChange: setMeatOptionInput,
  });
  const fishNameKeyboard = useSoftKeyboardField({
    id: 'menu-item-fish-name',
    type: 'alphanumeric',
    label: 'Fish name',
    value: fishNameInput,
    onChange: setFishNameInput,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    let next: string | number | boolean = value;
    if (isCheckbox) {
      next = (e.target as HTMLInputElement).checked;
    } else if (name === 'price' || name === 'dailyAvailabilityLimit') {
      next = parseFloat(value) || 0;
    } else if (name === 'categoryId') {
      next = value === '' ? (categories[0]?.id ?? 0) : /^\d+$/.test(String(value)) ? parseInt(String(value), 10) : value;
      const nextCategory = categoryForId(categories, next as number | string);
      if (!item) {
        setChannelAvailability(channelAvailabilityForNewItem(nextCategory));
      } else {
        const locked = new Set(channelsLockedByCategory(nextCategory));
        setChannelAvailability((prev) => {
          const current = readChannelAvailability(prev);
          for (const key of locked) current[key] = false;
          return current;
        });
      }
    }
    setFormData(prev => ({ ...prev, [name]: next }));
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploadError(null);
    setImageUploading(true);
    try {
      const { url, thumbUrl } = await uploadMenuItemImage(file, id ?? undefined);
      setFormData(prev => ({ ...prev, imageUrl: url, imageUrlThumb: thumbUrl }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setImageUploadError(message);
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };


  const handleAddMeatOption = () => {
    if (meatOptionInput.trim() && !formData.meatOptions.includes(meatOptionInput.trim())) {
      setFormData(prev => ({
        ...prev,
        meatOptions: [...prev.meatOptions, meatOptionInput.trim()],
      }));
      setMeatOptionInput('');
    }
  };

  const handleRemoveMeatOption = (option: string) => {
    setFormData(prev => ({
      ...prev,
      meatOptions: prev.meatOptions.filter(o => o !== option),
    }));
  };

  const handleSoldByWeightChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      soldByWeight: checked,
      price: checked ? 0 : prev.price,
    }));
    if (checked) {
      setChannelAvailability((prev) => ({
        ...readChannelAvailability(prev),
        availableOnline: false,
        availableQr: false,
        availableMealPlan: false,
      }));
    }
  };

  const handleAddFishName = () => {
    const name = fishNameInput.trim();
    if (!name) return;
    setFormData((prev) => {
      if (prev.weightFishOptions.some((existing) => existing.toLowerCase() === name.toLowerCase())) return prev;
      return { ...prev, weightFishOptions: [...prev.weightFishOptions, name] };
    });
    setFishNameInput('');
  };

  const handleRemoveFishName = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      weightFishOptions: prev.weightFishOptions.filter((existing) => existing !== name),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fishNames = formData.weightFishOptions.map((name) => name.trim()).filter(Boolean);
    if (!formData.name.trim()) {
      alert('Please fill in a name.');
      return;
    }
    if (formData.soldByWeight) {
      if (fishNames.length === 0) {
        alert('Add at least one fish name for a sold-by-weight item.');
        return;
      }
    } else if (formData.price <= 0) {
      alert('Please fill in a name and a valid price.');
      return;
    }
    // When editing, preserve existing image URLs if user didn't upload a new one
    const imageUrlToSave = formData.imageUrl || (item?.imageUrl ?? '');
    const imageUrlThumbToSave = formData.imageUrlThumb || (item?.imageUrlThumb ?? undefined);
    const channels = formData.soldByWeight
      ? {
          ...channelAvailability,
          availableOnline: false as const,
          availableQr: false as const,
          availableMealPlan: false as const,
        }
      : channelAvailability;
    const savedItem: MenuItem = { 
      ...formData, 
      id: id || 0,
      price: formData.soldByWeight ? 0 : formData.price,
      imageUrl: imageUrlToSave,
      imageUrlThumb: imageUrlThumbToSave || undefined,
      description: formData.description?.trim() || undefined,
      meatOptions: formData.soldByWeight || formData.meatOptions.length === 0 ? undefined : formData.meatOptions,
      soldByWeight: formData.soldByWeight,
      weightFishOptions: formData.soldByWeight ? fishNames : [],
      dietaryTags: formData.dietaryTags.length > 0 ? formData.dietaryTags : undefined,
      allergens: formData.allergens.length > 0 ? formData.allergens : undefined,
      allergyWarning: formData.allergyWarning.trim() || undefined,
      // Normalize availability flag: treat undefined as true
      isAvailable: formData.isAvailable !== false,
      ...channels,
    };
    
    // Only include daily availability fields if enabled
    if (formData.hasDailyAvailability) {
      savedItem.hasDailyAvailability = true;
      savedItem.dailyAvailabilityLimit = formData.dailyAvailabilityLimit || 0;
      // If this is a new item or availability hasn't been set, initialize dailyAvailability to limit
      if (!item || item.dailyAvailability === undefined) {
        savedItem.dailyAvailability = formData.dailyAvailabilityLimit || 0;
        savedItem.lastAvailabilityReset = Date.now();
      } else {
        // Preserve existing dailyAvailability and lastAvailabilityReset when editing
        savedItem.dailyAvailability = item.dailyAvailability;
        savedItem.lastAvailabilityReset = item.lastAvailabilityReset;
      }
    } else {
      // Disable daily availability explicitly and clear related fields
      savedItem.hasDailyAvailability = false;
      savedItem.dailyAvailability = undefined;
      savedItem.dailyAvailabilityLimit = undefined;
      savedItem.lastAvailabilityReset = undefined;
    }
    
    onSave(savedItem);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{item ? 'Edit Menu Item' : 'Add New Menu Item'}</h2>
          <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input type="text" name="name" id="name" value={formData.name} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500" data-soft-keyboard-handled {...nameKeyboard} required />
          </div>
          <div className="mb-4 p-4 bg-sky-50 border border-sky-200 rounded-lg">
            <label htmlFor="soldByWeight" className="flex items-center">
              <input
                type="checkbox"
                id="soldByWeight"
                checked={formData.soldByWeight}
                onChange={(e) => handleSoldByWeightChange(e.target.checked)}
                className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Sold by weight</span>
            </label>
            <p className="mt-1 text-xs text-gray-600 ml-6">
              Till staff pick a fish, then enter the weight in kg and the price per kg. The line total is calculated. Online, QR, and meal plan stay off.
            </p>
          </div>
          {!formData.soldByWeight && (
          <div className="mb-4">
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">Price</label>
            <input type="number" name="price" id="price" value={formData.price} step="0.01" min="0" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500" data-soft-keyboard-handled {...priceKeyboard} required />
          </div>
          )}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (shown to customers)
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              data-soft-keyboard-handled
              {...descriptionKeyboard}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
              placeholder="Short description of the dish (e.g., ingredients, style, spice level)."
            />
            <p className="mt-1 text-xs text-gray-500">
              This will appear in the customer menu (online / QR) under the item name.
            </p>
          </div>
          <div className="mb-4">
            <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select name="categoryId" id="categoryId" value={String(formData.categoryId)} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500">
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="isAvailable" className="flex items-center">
              <input
                type="checkbox"
                name="isAvailable"
                id="isAvailable"
                checked={formData.isAvailable}
                onChange={handleChange}
                className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Item is available for ordering</span>
            </label>
          </div>
          <MenuChannelAvailabilityFields
            value={channelAvailability}
            onChange={setChannelAvailability}
            disabled={!formData.isAvailable}
            lockedOff={[
              ...channelsLockedByCategory(categoryForId(categories, formData.categoryId)),
              ...(formData.soldByWeight
                ? (['availableOnline', 'availableQr', 'availableMealPlan'] as const)
                : []),
            ]}
            lockedOffNote={formData.soldByWeight ? 'not sold by weight here' : undefined}
            helperText={
              formData.soldByWeight
                ? 'Sold by weight is till-only. Online, QR, and meal plan stay off. Dine-in, takeaway, and delivery can stay on.'
                : 'Nothing is ticked until you choose it. If this category is only on some places, those start ticked and the rest stay off.'
            }
          />
          <div className="mb-4">
            <label htmlFor="isSignatureDish" className="flex items-center">
              <input type="checkbox" name="isSignatureDish" id="isSignatureDish" checked={formData.isSignatureDish} onChange={handleChange} className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500" />
              <span className="ml-2 text-sm font-medium text-gray-700">Kings Park Signature Dish</span>
            </label>
          </div>
          <div className="mb-4">
            <span className="block text-sm font-medium text-gray-700 mb-1">Dietary tags</span>
            <p className="text-xs text-gray-500 mb-2">Shown as badges and filters in the customer app.</p>
            <div className="flex flex-wrap gap-2">
              {DIETARY_TAGS.map((tag) => {
                const selected = formData.dietaryTags.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setFormData((prev) => ({ ...prev, dietaryTags: toggle(prev.dietaryTags, tag.id) }))}
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${selected ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    {tag.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mb-4">
            <span className="block text-sm font-medium text-gray-700 mb-1">Allergens</span>
            <p className="text-xs text-gray-500 mb-2">Customers who saved a matching allergy are warned before ordering.</p>
            <div className="flex flex-wrap gap-2">
              {COMMON_ALLERGENS.map((allergen) => {
                const selected = formData.allergens.includes(allergen);
                return (
                  <button
                    key={allergen}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setFormData((prev) => ({ ...prev, allergens: toggle(prev.allergens, allergen) }))}
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${selected ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    {allergen}
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              name="allergyWarning"
              value={formData.allergyWarning}
              onChange={handleChange}
              placeholder="Extra allergy note (optional), e.g. May contain traces of nuts"
              className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 text-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            />
          </div>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label htmlFor="hasDailyAvailability" className="flex items-center mb-3">
              <input 
                type="checkbox" 
                name="hasDailyAvailability" 
                id="hasDailyAvailability" 
                checked={formData.hasDailyAvailability} 
                onChange={handleChange} 
                className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500" 
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Enable Daily Availability</span>
            </label>
            <p className="text-xs text-gray-600 mb-3 ml-6">Track how many plates are available for the day. Availability will automatically reset at midnight.</p>
            {formData.hasDailyAvailability && (
              <div className="ml-6">
                <label htmlFor="dailyAvailabilityLimit" className="block text-sm font-medium text-gray-700 mb-1">
                  Available Quantity (per day)
                </label>
                <input 
                  type="number" 
                  name="dailyAvailabilityLimit" 
                  id="dailyAvailabilityLimit" 
                  value={formData.dailyAvailabilityLimit || ''} 
                  min="0" 
                  step="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500" 
                  placeholder="e.g., 50"
                  data-soft-keyboard-handled
                  {...dailyLimitKeyboard}
                />
                <p className="text-xs text-gray-500 mt-1">Number of plates available for today. This will reset automatically at midnight.</p>
                {item && item.hasDailyAvailability && item.dailyAvailability !== undefined && (
                  <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                    <p className="text-xs text-gray-600">
                      <span className="font-semibold">Current Availability:</span> {item.dailyAvailability} / {item.dailyAvailabilityLimit || 0}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          {formData.soldByWeight && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Fish names</label>
            <p className="text-xs text-gray-500 mb-2">Staff choose one of these at the till. The price per kg is typed on each sale.</p>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={fishNameInput}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFishName())}
                placeholder="e.g., Snapper"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                data-soft-keyboard-handled
                {...fishNameKeyboard}
              />
              <button
                type="button"
                onClick={handleAddFishName}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm font-medium"
              >
                Add
              </button>
            </div>
            {formData.weightFishOptions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.weightFishOptions.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-sky-100 text-sky-800"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => handleRemoveFishName(name)}
                      className="ml-2 text-sky-700 hover:text-sky-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          )}
          {!formData.soldByWeight && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Options (Optional)</label>
            <p className="text-xs text-gray-500 mb-2">Add options that customers can choose from (e.g., Beef, Chicken, Vegetarian, Mild, Spicy)</p>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={meatOptionInput}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMeatOption())}
                placeholder="e.g., Beef, Chicken, Vegetarian"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                data-soft-keyboard-handled
                {...meatOptionKeyboard}
              />
              <button
                type="button"
                onClick={handleAddMeatOption}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm font-medium"
              >
                Add
              </button>
            </div>
            {formData.meatOptions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.meatOptions.map((option, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-pink-100 text-pink-800"
                  >
                    {option}
                    <button
                      type="button"
                      onClick={() => handleRemoveMeatOption(option)}
                      className="ml-2 text-pink-600 hover:text-pink-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          )}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Image (Optional)</label>
            <div className="mt-1 flex items-center space-x-4">
              <MenuItemImage
                src={formData.imageUrl || `https://picsum.photos/seed/${formData.name || 'placeholder'}/200`}
                alt="Preview"
                className="w-24 h-24 rounded-md"
                loading="eager"
                width={96}
                height={96}
              />
              <div>
                <input type="file" id="imageUpload" accept="image/*" onChange={handleFileChange} className="hidden" disabled={imageUploading}/>
                <label htmlFor="imageUpload" className={`inline-block py-2 px-3 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 ${imageUploading ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed' : 'cursor-pointer bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {imageUploading ? 'Uploading…' : 'Upload Image'}
                </label>
                {imageUploadError && <p className="mt-1 text-sm text-red-600">{imageUploadError}</p>}
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-4">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600">Save Item</button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default MenuItemModal;